import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getStripe } from '~/lib/stripe';
import { friendlyStripeError } from '~/lib/stripeErrors';
import { apiGetBillingConfig, apiGetBillingOverview, apiPayCreditTopUp, type BillingOverview } from '~/lib/api/billing';
import { AccessibleModal } from '~/components/account/AccessibleModal';
import { PaymentMethodModal } from '~/components/account/PaymentMethodModal';
import { formatCredits } from '~/lib/plans-model';

function extractApiError(err: any, fallback: string): string {
  // Log the raw error for debugging; never surface axios/backend technical text to users.
  console.error(err);

  const apiMessage = err?.response?.data?.message;

  return (Array.isArray(apiMessage) ? apiMessage.join(', ') : apiMessage) || fallback;
}

function fmtMoney(amount: number, currency = 'usd'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="flex-shrink-0 text-[12px] text-muted">{label}</span>
      <span className="min-w-0 text-right text-[12.5px] text-primary">{value}</span>
    </div>
  );
}

interface TopUpConfirmModalProps {
  amount: number;
  credits: number;
  onPaid: () => void;
  onClose: () => void;
}

/**
 * In-app top-up confirmation (Phase 3). Shows the amount, credits, and the saved
 * card; lets the user change the card (reusing PaymentMethodModal) and pays in
 * place — no redirect. Mirrors the subscription upgrade flow: the saved card is
 * charged and 3D Secure, if required, is completed here via the client secret.
 * Credits are granted server-side by the webhook.
 */
export function TopUpConfirmModal({ amount, credits, onPaid, onClose }: TopUpConfirmModalProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [changingCard, setChangingCard] = useState(false);

  const { data: config } = useQuery({
    queryKey: ['billing-config'],
    queryFn: apiGetBillingConfig,
    staleTime: Infinity,
  });
  const { data: overview } = useQuery<BillingOverview>({
    queryKey: ['billing-overview'],
    queryFn: apiGetBillingOverview,
    staleTime: 30_000,
  });

  const publishableKey = config?.publishableKey ?? null;
  const card = overview?.paymentMethod;

  const pay = useMutation({
    mutationFn: async () => {
      const result = await apiPayCreditTopUp(amount);

      if (result.status === 'succeeded') {
        return;
      }

      if (result.status === 'requires_action') {
        if (!result.clientSecret || !publishableKey) {
          throw new Error('This card needs verification, which can’t be completed here. Please try another card.');
        }

        const stripe = await getStripe(publishableKey);

        if (!stripe) {
          throw new Error('Could not load the secure payment library. Please try again.');
        }

        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(result.clientSecret);

        if (confirmError) {
          throw new Error(friendlyStripeError(confirmError, 'Your payment could not be completed. Please try again.'));
        }

        if (paymentIntent && paymentIntent.status !== 'succeeded') {
          throw new Error('Payment was not completed. Please try again.');
        }

        return;
      }

      throw new Error('Your payment could not be completed. Please check your card and try again.');
    },
    onSuccess: () => {
      onPaid();
      onClose();
    },
    onError: (err) => setError(extractApiError(err, 'Your purchase could not be completed.')),
  });

  const busy = pay.isPending;

  const afterPayment = [
    'Credits are added instantly after successful payment.',
    'Your subscription plan remains unchanged.',
    'This is a one-time purchase, not a recurring subscription.',
    'A receipt is emailed to you, and the purchase appears in your credit history.',
  ];

  return (
    <>
      <AccessibleModal
        title="Buy Credits"
        description="Purchase additional credits for your Dunea account. Credits are added immediately after your payment is confirmed and never expire."
        maxWidth={500}
        dismissible={!busy}
        onClose={onClose}
      >
        <>
          <div className="mb-4 flex items-center justify-center gap-2">
            <img src="/dunea-mark.svg" width={22} height={22} alt="" className="block" />
            <span className="ui-brand text-[16px] leading-none text-primary">Dunea</span>
          </div>

          <h3 className="ui-serif m-0 mb-1.5 text-[21px]">Buy credits</h3>
          <p className="m-0 mb-5 text-[12.5px] leading-[1.55] text-muted">
            Purchase additional credits for your Dunea account. Credits are added immediately after your payment is
            confirmed and never expire.
          </p>

          {/* Purchase details */}
          <div className="rounded-[10px] border border-hairline bg-raised px-4 py-1.5">
            <Row label="Top-up amount" value={fmtMoney(amount)} />
            <Row
              label="Credits you'll receive"
              value={
                <span>
                  <span className="ui-mono text-accent">+{formatCredits(credits)}</span> credits
                </span>
              }
            />
            <Row
              label="Payment method"
              value={
                <span className="inline-flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
                  <span className="whitespace-nowrap">
                    {card ? `${card.brand} •••• ${card.last4}` : 'No card on file'}
                  </span>
                  <button
                    onClick={() => setChangingCard(true)}
                    className="ui-btn-ghost px-2 py-0.5 text-[11px]"
                    disabled={busy}
                  >
                    {card ? 'Update card' : 'Add card'}
                  </button>
                </span>
              }
            />
          </div>

          {/* Total */}
          <div className="flex items-baseline justify-between gap-3 px-4 pb-1 pt-4">
            <span className="text-[14px] font-medium text-primary">Total</span>
            <span className="ui-serif text-[26px] tracking-[-0.015em] text-accent">{fmtMoney(amount)}</span>
          </div>

          {/* What happens after payment */}
          <div className="mt-3.5 rounded-[10px] border border-hairline bg-raised px-3.5 py-3">
            <div className="ui-caption mb-2.5">What happens after payment?</div>
            {afterPayment.map((line) => (
              <div key={line} className="flex items-start gap-2 py-0.5">
                <span className="i-ph:check-circle-fill mt-px flex-shrink-0 text-[14px] text-success" />
                <span className="text-[12px] leading-normal text-secondary">{line}</span>
              </div>
            ))}
          </div>

          {/* Stripe reassurance */}
          <div className="mt-3.5 flex items-center justify-center gap-1.5 text-[11.5px] text-muted">
            <span className="i-ph:lock-simple text-[13px]" />
            Secure payment powered by Stripe.
          </div>

          {!card && (
            <div className="mt-3 text-[12px] leading-normal text-warning">Add a payment method to continue.</div>
          )}

          {error && <div className="mt-3.5 text-[12px] leading-normal text-danger">{error}</div>}

          <div className="mt-5 flex justify-end gap-2">
            <button onClick={onClose} className="ui-btn-ghost" disabled={busy}>
              Cancel
            </button>
            <button
              onClick={() => {
                setError(null);
                pay.mutate();
              }}
              className="ui-btn-accent"
              disabled={busy || !card}
            >
              {busy ? 'Processing…' : `Pay ${fmtMoney(amount)}`}
            </button>
          </div>
        </>
      </AccessibleModal>

      {changingCard && publishableKey && (
        <PaymentMethodModal
          publishableKey={publishableKey}
          hasExistingCard={Boolean(card)}
          onClose={() => {
            setChangingCard(false);
            // PaymentMethodModal invalidates billing-overview on success; ensure the
            // card summary here reflects any change.
            queryClient.invalidateQueries({ queryKey: ['billing-overview'] });
          }}
        />
      )}
    </>
  );
}
